"use client";

import React, { ReactNode } from "react";
import { Drawer as DrawerPrimitive } from "vaul";

// Configuration constants - keeping at top for easy customization
const DRAWER_CONFIG = {
  // Maximum height as percentage of viewport
  MAX_HEIGHT_VH: 70,
  // Background overlay opacity
  OVERLAY_OPACITY: 0.8,

  Z_INDEX: 1100,
} as const;

interface DrawerProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const Drawer: React.FC<DrawerProps> = ({
  children,
  isOpen,
  onClose,
  className = "",
}) => {
  return (
    <DrawerPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 bg-black/80 outline-none focus:outline-none"
          style={{ zIndex: DRAWER_CONFIG.Z_INDEX }}
        />
        <DrawerPrimitive.Content
          className="flex flex-col fixed bottom-0 left-0 right-0 rounded-t-[10px] outline-none focus:outline-none"
          style={{
            maxHeight: `${DRAWER_CONFIG.MAX_HEIGHT_VH}%`,
            zIndex: DRAWER_CONFIG.Z_INDEX,
          }}
        >
          <div className="flex-shrink-0 mx-auto w-12 h-1.5 rounded-full bg-zinc-300 mt-4 mb-2" />
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#4a4a4a transparent",
            }}
          >
            <style
              dangerouslySetInnerHTML={{
                __html: `
                div::-webkit-scrollbar {
                  width: 8px;
                }
                div::-webkit-scrollbar-track {
                  background: transparent;
                }
                div::-webkit-scrollbar-thumb {
                  background-color: #4a4a4a;
                  border-radius: 4px;
                }
                div::-webkit-scrollbar-thumb:hover {
                  background-color: #5a5a5a;
                }
              `,
              }}
            />
            <div
              className={`${className}`}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              data-vaul-no-drag="true"
            >
              {children}
            </div>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
};

// Compound component pattern for more flexibility
const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerClose = DrawerPrimitive.Close;
const DrawerTitle = DrawerPrimitive.Title;
const DrawerDescription = DrawerPrimitive.Description;

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerTitle,
  DrawerDescription,
  type DrawerProps,
};

export default Drawer;
